import { useEffect, useLayoutEffect, useRef } from 'react';

export type HotkeyHandler = (e: KeyboardEvent) => void;
export type HotkeyMap = Record<string, HotkeyHandler>;

interface ParsedCombo {
  key: string;
  mod: boolean;
  shift: boolean;
  alt: boolean;
}

function parseCombo(combo: string): ParsedCombo {
  const parts = combo.toLowerCase().split('+').map((p) => p.trim());
  const key = parts.pop() ?? '';
  return {
    key,
    mod: parts.includes('mod'),
    shift: parts.includes('shift'),
    alt: parts.includes('alt'),
  };
}

// Foco em campo editável bloqueia hotkeys SEM modificador (P9: o mod tá digitando).
// Combos com mod+ passam mesmo no foco do composer.
function isEditableActiveElement(): boolean {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const ce = el.getAttribute('contenteditable');
  if (ce !== null && ce !== 'false') return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function useGlobalHotkeys(
  map: HotkeyMap,
  opts: { enabled?: boolean } = {},
) {
  const { enabled = true } = opts;
  const mapRef = useRef(map);
  // useLayoutEffect em vez de mutação durante render: garante consistência em
  // Concurrent Rendering (React 18), onde um render pode ser descartado e
  // reiniciado — mutações inline ficariam aplicadas mesmo no render abortado.
  useLayoutEffect(() => {
    mapRef.current = map;
  });

  useEffect(() => {
    if (!enabled) return;
    function handler(e: KeyboardEvent) {
      const editable = isEditableActiveElement();
      const pressedKey = e.key.toLowerCase();
      const hasMod = e.metaKey || e.ctrlKey;
      for (const [combo, fn] of Object.entries(mapRef.current)) {
        const { key, mod, shift, alt } = parseCombo(combo);
        if (key !== pressedKey) continue;
        // mod gateia bidirecional: combo mod+ exige mod; combo simples sem
        // mod tb não pode disparar quando algum mod está apertado.
        if (mod !== hasMod) continue;
        // shift/alt gateiam só quando declarados — caracteres como '?' são
        // produzidos por Shift+/ no QWERTY, e bloquear na ausência de
        // declaração quebraria o caso natural.
        if (shift && !e.shiftKey) continue;
        if (alt && !e.altKey) continue;
        if (editable && !mod) continue;
        e.preventDefault();
        fn(e);
        return;
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled]);
}
